import React from "react";
import PropTypes from "prop-types";
import { timeFormat } from "d3-time-format";
import { OHLCTooltip, SingleValueTooltip } from "@chartLib/lib/tooltip";
import GenericChartComponent from "@chartLib/lib/GenericChartComponent";
import { formatPrice } from "../../utils/priceUtils";
import { formatNumber } from "../../utils/numberFormatting";
import { getMALabel, MA_TYPES } from "@chartLib/lib/utils";
import { getSourcePathLabel } from "@chartLib/lib/utils/getSourcePath";
import ChartButtonsTooltip from "./ChartButtonsTooltip";
import ChartResizeHandle from "./ChartResizeHandle"; // CHECKPOINT: Handle component ready
import "./ChartTooltips.css";

/**
 * CHECKPOINT: ChartTooltips - Integração preservada
 *
 * STATUS: ✅ ChartResizeHandle importado e renderizado
 * PROBLEMA: ⚠️ Callbacks (onStartResize, onResize, onEndResize) não chegam do ChartWrapper
 *
 * TODO: ChartWrapper.jsx precisa implementar callbacks e passar via props
 * VER: docs/situacao-atual.md para recovery steps
 */

// Component that consolidates tooltip rendering for main and indicator charts
const ChartTooltips = ({
  config,
  indicatorRegistry,
  themeColors,
  xFormat,
  periodicity,
  chartId, // intentionally accepted for future contextual tooltip logic
  chartSymbol,
  sectionIndex,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  indicatorMenuRef,
  // Resize handle props
  isLastSection,
  onStartResize,
  onResize,
  onEndResize,
  onResetSize,
}) => {
  // chartId is used by ChartResizeHandle for scoped global registry
  // Tooltip positioning constants
  const INDICATOR_TOOLTIP_ORIGIN = [8, 8];

  // Helper: ensure a color string has full alpha (1) when possible
  const opaqueColor = (color) => {
    if (!color || typeof color !== "string") return color;
    const c = color.trim();
    if (c.startsWith("rgba(")) {
      // replace final alpha with 1
      const parts = c.replace(/rgba\(|\)/g, "").split(",");
      parts[3] = " 1";
      return `rgba(${parts.join(",")})`;
    }
    if (c.startsWith("rgb(")) {
      const parts = c.replace(/rgb\(|\)/g, "").split(",");
      return `rgba(${parts.join(",")},1)`;
    }
    return c; // hex or named color - return as is
  };

  // Helper: Extract value fill color from appearance config
  const getValueFillColor = (appearanceConfig) => {
    if (!appearanceConfig) return themeColors.text;

    // Try different appearance config properties in order of preference
    if (appearanceConfig.stroke?.color) return appearanceConfig.stroke.color;
    if (appearanceConfig.color?.color) return appearanceConfig.color.color;
    if (appearanceConfig.upColor?.color) return appearanceConfig.upColor.color;
    if (appearanceConfig.fill?.color) return appearanceConfig.fill.color;

    return themeColors.text;
  };

  // Helper: Create chart buttons tooltip (edit removed - tooltips are now clickable, delete removes all)
  const createChartButtonsTooltip = (
    chartType,
    indicatorConfig,
    panelIndicators,
  ) => {
    const isIndicator =
      chartType === "Indicator" &&
      (!!indicatorConfig || (panelIndicators && panelIndicators.length > 0));
    const indicators =
      panelIndicators || (indicatorConfig ? [indicatorConfig] : []);

    // Delete all indicators on this panel
    const handleDeleteAll = () => {
      if (indicators.length > 0) {
        // Remove all indicators from this panel
        // Remove in reverse order to handle dependencies (leaf nodes first)
        // This way if Volume → EMA → WMA, we remove WMA, then EMA, then Volume
        const reversedIndicators = [...indicators].reverse();

        reversedIndicators.forEach((indicator) => {
          if (indicatorMenuRef?.current?.remove) {
            indicatorMenuRef.current.remove(indicator);
          } else {
            console.error(
              "[ChartTooltips] indicatorMenuRef.current.remove is not available!",
            );
          }
        });
      }
    };

    return (
      <ChartButtonsTooltip
        key={`${chartType}-chart-buttons`}
        origin={(width) => [width - 50, 6]}
        arrowFill={themeColors.text}
        onUpClick={onMoveUp}
        onDownClick={onMoveDown}
        showHorizontalLine={false}
        horizontalLineStrokeWidth={1}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        showEdit={false} // Disabled - tooltips are now clickable for editing
        showDelete={true} // Show delete button on all charts
        onEdit={undefined}
        onDelete={handleDeleteAll} // Delete all indicators (or all non-OHLC indicators on main chart)
      />
    );
  };

  // Helper: Wrap indicator tooltip with buttons and resize handle
  const wrapWithButtons = (tooltipElement) => {
    // Get all indicators in this panel (Phase 3: Series-as-Source support)
    const panelIndicators =
      config.indicators || (config.indicator ? [config.indicator] : []);

    return (
      <>
        {tooltipElement}
        {createChartButtonsTooltip(
          "Indicator",
          config.indicator,
          panelIndicators,
        )}
        {/* Resize handle at the bottom of the section (only if not last section) */}
        {!isLastSection && (
          <ChartResizeHandle
            chartId={chartId}
            sectionIndex={sectionIndex}
            disabled={isLastSection}
            onStartResize={onStartResize}
            onResize={onResize}
            onEndResize={onEndResize}
            onReset={onResetSize}
          />
        )}
      </>
    );
  };

  const findRegistryEntry = (indicatorKey) => {
    for (const categoryKey of Object.keys(indicatorRegistry)) {
      const entry = indicatorRegistry[categoryKey].indicators[indicatorKey];
      if (entry) return entry;
    }
    return null;
  };

  // Helper: Create onClick handler for indicator tooltips
  const createIndicatorOnClick = (indicator) => {
    return (e) => {
      if (indicatorMenuRef?.current?.edit) {
        indicatorMenuRef.current.edit(indicator);
      }
    };
  };

  // Main chart tooltips (OHLC, moving averages, bollinger)
  if (config.type === "main") {
    // Dynamic tooltip positioning system
    // Start at Y=4 to prevent clipping on iPad (Safari has different top edge handling)
    let currentY = 4; // Starting Y position for OHLC
    const tooltipSpacing = 24; // Space between tooltips
    const tooltips = [];

    // OHLC tooltip uses two rows when chart is narrow (<400px), adding extra height.
    // All subsequent tooltips use function origins to shift down dynamically.
    const OHLC_TWO_ROW_THRESHOLD = 400;
    const OHLC_EXTRA_ROW_HEIGHT = 18;
    const ohlcYOffset = (chartWidth) =>
      chartWidth < OHLC_TWO_ROW_THRESHOLD ? OHLC_EXTRA_ROW_HEIGHT : 0;
    const dynamicOrigin = (baseY) => (chartWidth) => [
      8,
      baseY + ohlcYOffset(chartWidth),
    ];

    // Always include OHLC tooltip first
    // Increase contrast: subdued labels, prominent values
    const isDarkMode = themeColors.isDark; // Check if dark mode
    const labelColor = isDarkMode ? "#9CA3AF" : themeColors.crosshair; // Lighter gray for dark mode, crosshair (#999999) for light
    const valueColor = isDarkMode ? "#ffffff" : "#000000"; // Very prominent (pure white/black)
    // Use chart background color #1f2937 with transparency
    const tooltipBgFill = themeColors.tooltipBg;

    tooltips.push(
      <OHLCTooltip
        key="ohlc"
        origin={[8, currentY]}
        className="ohlc-tooltip"
        textFill={valueColor}
        labelFill={labelColor}
        bgFill={tooltipBgFill}
        xDisplayFormat={(date) => {
          // Define intraday periods that should show time
          const intradayPeriods = [
            "1min",
            "2min",
            "3min",
            "4min",
            "5min",
            "10min",
            "15min",
            "20min",
            "30min",
            "60min",
            "120min",
            "240min",
          ];
          const isIntraday = intradayPeriods.includes(periodicity);

          if (isIntraday) {
            // For intraday: show date + time
            return timeFormat("%d/%m/%Y %H:%M")(date);
          } else {
            // For daily and above: show only date
            return timeFormat("%d/%m/%Y")(date);
          }
        }}
        ohlcFormat={(price) => formatPrice(chartSymbol, price)}
        volumeFormat={(d) =>
          d.toLocaleString("pt-BR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
        }
      />,
    );

    // Moving averages tooltip - Use centralized MA_TYPES for consistency
    const maTypeKeys = MA_TYPES.map((ma) => ma.value);
    const movingAverageIndicators =
      config.indicators?.filter((ind) => maTypeKeys.includes(ind.key)) || [];

    if (movingAverageIndicators.length > 0) {
      const MovingAverageTooltip = indicatorRegistry.movingAverages?.tooltip;
      if (MovingAverageTooltip) {
        const tooltipOptions = movingAverageIndicators.map((indicator) => ({
          yAccessor: (d) => d[indicator.renderKey],
          type: getMALabel(indicator.key), // Use centralized label getter
          stroke: indicator.appearanceConfig?.stroke?.color || "#2196F3",
          windowSize: indicator.config?.windowSize || 10,
          sourcePath: indicator.config?.sourcePath,
          sourceLabel: getSourcePathLabel(
            indicator.config?.sourcePath,
            config.indicators,
          ),
          echo: indicator,
        }));

        tooltips.push(
          <MovingAverageTooltip
            key="ma"
            origin={dynamicOrigin(30)}
            className="moving-average-tooltip"
            options={tooltipOptions}
            textFill={themeColors.text}
            labelFill={themeColors.textSecondary}
            bgFill={tooltipBgFill}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={(maOption) => {
              // maOption contains the echo field with the indicator object
              const indicator = maOption.echo;
              indicatorMenuRef?.current?.edit(indicator);
            }}
            data-evo-id="charttooltips-ma-tooltip"
          />,
        );
        // Moving averages take more space (multiline), so give it extra room
        currentY = 70; // Jump to 100 for next tooltip after moving averages
      }
    } else currentY += 30;

    // Bollinger Bands tooltip
    const bollingerIndicator = config.indicators?.find(
      (ind) => ind.key === "bollingerBand",
    );
    if (bollingerIndicator) {
      const BollingerBandTooltip =
        indicatorRegistry.volatility?.indicators.bollingerBand?.tooltip;
      if (BollingerBandTooltip) {
        tooltips.push(
          <BollingerBandTooltip
            key="bb"
            yAccessor={(d) => d[bollingerIndicator.renderKey]}
            options={{
              sourcePath: bollingerIndicator.config?.sourcePath || "close",
              windowSize: bollingerIndicator.config?.windowSize || 20,
              multiplier: bollingerIndicator.config?.multiplier || 2,
              movingAverageType:
                bollingerIndicator.config?.movingAverageType || "sma",
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            bgFill={tooltipBgFill}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(bollingerIndicator)}
            data-evo-id="charttooltips-btn-bollinger"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // CDP (Média HLC e Desvios) tooltip — overlay de preço, 5 linhas
    const cdpIndicator = config.indicators?.find((ind) => ind.key === "cdp");
    if (cdpIndicator) {
      const CdpTooltip = indicatorRegistry.volatility?.indicators.cdp?.tooltip;
      if (CdpTooltip) {
        tooltips.push(
          <CdpTooltip
            key="cdp"
            yAccessor={(d) => d[cdpIndicator.renderKey]}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(cdpIndicator)}
            data-evo-id="charttooltips-btn-cdp"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // SR5 (Suporte e Resistência 5) tooltip — overlay de preço, 10 linhas
    const sr5Indicator = config.indicators?.find((ind) => ind.key === "sr5");
    if (sr5Indicator) {
      const Sr5Tooltip = indicatorRegistry.volatility?.indicators.sr5?.tooltip;
      if (Sr5Tooltip) {
        tooltips.push(
          <Sr5Tooltip
            key="sr5"
            yAccessor={(d) => d[sr5Indicator.renderKey]}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(sr5Indicator)}
            data-evo-id="charttooltips-btn-sr5"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // MIKE (Maiores Altas e Baixas com a Média HLC) tooltip — overlay, 6 linhas
    const mikeIndicator = config.indicators?.find((ind) => ind.key === "mike");
    if (mikeIndicator) {
      const MikeTooltip =
        indicatorRegistry.volatility?.indicators.mike?.tooltip;
      if (MikeTooltip) {
        tooltips.push(
          <MikeTooltip
            key="mike"
            yAccessor={(d) => d[mikeIndicator.renderKey]}
            options={{
              period: mikeIndicator.config?.period || 12,
              ...mikeIndicator.options,
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(mikeIndicator)}
            data-evo-id="charttooltips-btn-mike"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // BBIBOLL (Bullish-Bear Bollinger) tooltip — overlay de preço, 3 linhas
    const bbiBollIndicator = config.indicators?.find(
      (ind) => ind.key === "bbiBoll",
    );
    if (bbiBollIndicator) {
      const BbiBollTooltip =
        indicatorRegistry.volatility?.indicators.bbiBoll?.tooltip;
      if (BbiBollTooltip) {
        tooltips.push(
          <BbiBollTooltip
            key="bbiBoll"
            yAccessor={(d) => d[bbiBollIndicator.renderKey]}
            options={{
              period: bbiBollIndicator.config?.period || 10,
              multiplier: bbiBollIndicator.config?.multiplier || 3,
              ...bbiBollIndicator.options,
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(bbiBollIndicator)}
            data-evo-id="charttooltips-btn-bbiboll"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // Donchian Channel tooltip
    const donchianIndicator = config.indicators?.find(
      (ind) => ind.key === "donchianChannel",
    );
    if (donchianIndicator) {
      const DonchianChannelTooltip =
        indicatorRegistry.volatility?.indicators.donchianChannel?.tooltip;
      if (DonchianChannelTooltip) {
        tooltips.push(
          <DonchianChannelTooltip
            key="donchian"
            yAccessor={(d) => d[donchianIndicator.renderKey]}
            options={{
              highPeriod: donchianIndicator.config?.highPeriod || 20,
              lowPeriod: donchianIndicator.config?.lowPeriod || 20,
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(donchianIndicator)}
            data-evo-id="charttooltips-btn-donchian"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    const hiloIndicator = config.indicators?.find(
      (ind) => ind.key === "hiloActivator",
    );
    if (hiloIndicator) {
      const registryEntry = findRegistryEntry(hiloIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        const appearanceConfig = hiloIndicator.appearanceConfig || {};
        const longColor = appearanceConfig.longStroke?.color || "#2ecc71";
        const shortColor = appearanceConfig.shortStroke?.color || "#ff4d4d";

        tooltips.push(
          <TooltipComponent
            key="hilo"
            yAccessor={(d) => d[hiloIndicator.renderKey]}
            options={{
              highPeriod: hiloIndicator.config?.highPeriod || 3,
              lowPeriod: hiloIndicator.config?.lowPeriod || 3,
              ...hiloIndicator.options,
            }}
            longFill={longColor}
            shortFill={shortColor}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(hiloIndicator)}
            data-evo-id="charttooltips-btn-hilo"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    const supertrendIndicator = config.indicators?.find(
      (ind) => ind.key === "supertrend",
    );
    if (supertrendIndicator) {
      const registryEntry = findRegistryEntry(supertrendIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="supertrend"
            yAccessor={(d) => {
              const node = d?.[supertrendIndicator.renderKey];
              if (!node || typeof node !== "object") return undefined;
              return Number.isFinite(node.value) ? node.value : undefined;
            }}
            options={{
              period: supertrendIndicator.config?.period ?? 7,
              multiplier: supertrendIndicator.config?.multiplier ?? 3,
              ...supertrendIndicator.options,
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            valueFill={getValueFillColor(supertrendIndicator.appearanceConfig)}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={(e) => {
              if (indicatorMenuRef?.current?.edit) {
                indicatorMenuRef.current.edit(supertrendIndicator);
              }
            }}
            {...(registryEntry.tooltipProps || {})}
            data-evo-id="charttooltips-supertrend-tooltip"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    const keltnerIndicator = config.indicators?.find(
      (ind) => ind.key === "keltner",
    );
    if (keltnerIndicator) {
      const registryEntry = findRegistryEntry(keltnerIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="keltner"
            yAccessor={(d) =>
              d[keltnerIndicator.renderKey] || d[keltnerIndicator.key]
            }
            options={{
              sourcePath: keltnerIndicator.config?.sourcePath || "close",
              period: keltnerIndicator.config?.period || 50,
              multiplier: keltnerIndicator.config?.multiplier || 5,
              movingAverageType:
                keltnerIndicator.config?.movingAverageType || "ema",
              ...keltnerIndicator.options,
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            valueFill={getValueFillColor(keltnerIndicator.appearanceConfig)}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(keltnerIndicator)}
            data-evo-id="charttooltips-btn-keltner"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    const starcBandsIndicator = config.indicators?.find(
      (ind) => ind.key === "starcBands",
    );
    if (starcBandsIndicator) {
      const registryEntry = findRegistryEntry(starcBandsIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="starcBands"
            yAccessor={(d) =>
              d[starcBandsIndicator.renderKey] || d[starcBandsIndicator.key]
            }
            options={{
              period: starcBandsIndicator.config?.period || 15,
              maPeriod: starcBandsIndicator.config?.maPeriod || 5,
              multiplier: starcBandsIndicator.config?.multiplier || 1.3,
              ...starcBandsIndicator.options,
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            valueFill={getValueFillColor(starcBandsIndicator.appearanceConfig)}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(starcBandsIndicator)}
            data-evo-id="charttooltips-btn-starc-bands"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    const maEnvelopeIndicator = config.indicators?.find(
      (ind) => ind.key === "maEnvelope",
    );
    if (maEnvelopeIndicator) {
      const registryEntry = findRegistryEntry(maEnvelopeIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="maEnvelope"
            yAccessor={(d) =>
              d[maEnvelopeIndicator.renderKey] || d[maEnvelopeIndicator.key]
            }
            options={{
              period: maEnvelopeIndicator.config?.period || 50,
              movingAverageType:
                maEnvelopeIndicator.config?.movingAverageType || "sma",
              shiftType: maEnvelopeIndicator.config?.shiftType || "percent",
              shift: maEnvelopeIndicator.config?.shift || 5,
              ...maEnvelopeIndicator.options,
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            valueFill={getValueFillColor(maEnvelopeIndicator.appearanceConfig)}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(maEnvelopeIndicator)}
            data-evo-id="charttooltips-btn-ma-envelope"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    const highLowIndicator = config.indicators?.find(
      (ind) => ind.key === "highLow",
    );
    if (highLowIndicator) {
      const registryEntry = findRegistryEntry(highLowIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="highLow"
            yAccessor={(d) =>
              d[highLowIndicator.renderKey] || d[highLowIndicator.key]
            }
            options={{
              period: highLowIndicator.config?.period || 14,
              ...highLowIndicator.options,
            }}
            appearance={{
              strokeHigh: highLowIndicator.appearanceConfig?.strokeHigh || {
                color: "#4CAF50",
              },
              strokeLow: highLowIndicator.appearanceConfig?.strokeLow || {
                color: "#F44336",
              },
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(highLowIndicator)}
            data-evo-id="charttooltips-btn-high-low"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // High & Low do Silverlight (médias de máx/mín) — ≠ highLow (HHV/LLV)
    const highLowMaIndicator = config.indicators?.find(
      (ind) => ind.key === "highLowMa",
    );
    if (highLowMaIndicator) {
      const registryEntry = findRegistryEntry(highLowMaIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="highLowMa"
            yAccessor={(d) =>
              d[highLowMaIndicator.renderKey] || d[highLowMaIndicator.key]
            }
            options={{
              mHigh: highLowMaIndicator.config?.mHigh || 20,
              mLow: highLowMaIndicator.config?.mLow || 20,
              ...highLowMaIndicator.options,
            }}
            appearance={{
              strokeHigh: highLowMaIndicator.appearanceConfig?.strokeHigh || {
                color: "#FFFF00",
              },
              strokeLow: highLowMaIndicator.appearanceConfig?.strokeLow || {
                color: "#FF0000",
              },
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(highLowMaIndicator)}
            data-evo-id="charttooltips-btn-high-low-ma"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    const hhvIndicator = config.indicators?.find((ind) => ind.key === "hhv");
    if (hhvIndicator) {
      const registryEntry = findRegistryEntry(hhvIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="hhv"
            yAccessor={(d) => d[hhvIndicator.renderKey] || d[hhvIndicator.key]}
            options={{
              period: hhvIndicator.config?.period || 14,
              ...hhvIndicator.options,
            }}
            appearance={{
              stroke: hhvIndicator.appearanceConfig?.stroke || {
                color: "#4CAF50",
              },
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(hhvIndicator)}
            data-evo-id="charttooltips-btn-hhv"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    const llvIndicator = config.indicators?.find((ind) => ind.key === "llv");
    if (llvIndicator) {
      const registryEntry = findRegistryEntry(llvIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="llv"
            yAccessor={(d) => d[llvIndicator.renderKey] || d[llvIndicator.key]}
            options={{
              period: llvIndicator.config?.period || 14,
              ...llvIndicator.options,
            }}
            appearance={{
              stroke: llvIndicator.appearanceConfig?.stroke || {
                color: "#F44336",
              },
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(llvIndicator)}
            data-evo-id="charttooltips-btn-llv"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    const atrStopIndicator = config.indicators?.find(
      (ind) => ind.key === "atrStop",
    );
    if (atrStopIndicator) {
      const registryEntry = findRegistryEntry(atrStopIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        const appearanceConfig = atrStopIndicator.appearanceConfig || {};

        tooltips.push(
          <TooltipComponent
            key="atr-stop"
            yAccessor={(d) => d[atrStopIndicator.renderKey]}
            options={{
              period: atrStopIndicator.config?.period ?? 21,
              multiplier: atrStopIndicator.config?.multiplier ?? 3,
              plotType: atrStopIndicator.config?.plotType ?? "points",
              useHighLow: atrStopIndicator.config?.useHighLow ?? false,
              ...atrStopIndicator.options,
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            valueFill={themeColors.text}
            buyColor={appearanceConfig.fillRising?.color}
            sellColor={appearanceConfig.fillFalling?.color}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(atrStopIndicator)}
            data-evo-id="charttooltips-btn-atr-stop"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // Parabolic SAR tooltip
    const sarIndicator = config.indicators?.find((ind) => ind.key === "sar");
    if (sarIndicator) {
      const SingleValueTooltip =
        indicatorRegistry.trend?.indicators.sar?.tooltip;
      if (SingleValueTooltip) {
        tooltips.push(
          <SingleValueTooltip
            key="sar"
            yAccessor={(d) => d[sarIndicator.renderKey]}
            options={{
              accelerationFactor:
                sarIndicator.config?.accelerationFactor || 0.02,
              maxAccelerationFactor:
                sarIndicator.config?.maxAccelerationFactor || 0.2,
              ...sarIndicator.options,
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            bgFill={tooltipBgFill}
            yLabel={sarIndicator.displayName || "SAR"}
            displayFormat={(price) => formatNumber(price, 2)}
            valueFill={(() => {
              if (!sarIndicator.appearanceConfig) return themeColors.text;

              const appearanceConfig = sarIndicator.appearanceConfig;

              // SAR uses fillRising/fillFalling colors
              if (appearanceConfig.fillRising?.color) {
                return appearanceConfig.fillRising.color;
              }

              if (appearanceConfig.stroke?.color) {
                return appearanceConfig.stroke.color;
              }

              return themeColors.text;
            })()}
            onClick={createIndicatorOnClick(sarIndicator)}
            data-evo-id="charttooltips-btn-sar"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // VWAP tooltip
    const vwapIndicator = config.indicators?.find((ind) => ind.key === "vwap");
    if (vwapIndicator) {
      const VWAPTooltip = indicatorRegistry.volume?.indicators.vwap?.tooltip;
      if (VWAPTooltip) {
        tooltips.push(
          <VWAPTooltip
            key="vwap"
            yAccessor={(d) => d[vwapIndicator.renderKey]}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            valueFill={
              vwapIndicator.appearanceConfig?.stroke?.color || themeColors.text
            }
            yLabel="VWAP"
            yDisplayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(vwapIndicator)}
            data-evo-id="charttooltips-btn-vwap"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // ZigZag tooltip
    const zigzagIndicator = config.indicators?.find(
      (ind) => ind.key === "zigzag",
    );
    if (zigzagIndicator) {
      const ZigZagTooltip = indicatorRegistry.trend?.indicators.zigzag?.tooltip;
      if (ZigZagTooltip) {
        tooltips.push(
          <ZigZagTooltip
            key="zigzag"
            yAccessor={(d) => d[zigzagIndicator.renderKey]}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            valueFill={
              zigzagIndicator.appearanceConfig?.stroke?.color ||
              themeColors.text
            }
            yLabel="ZigZag"
            yDisplayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(zigzagIndicator)}
            data-evo-id="charttooltips-btn-zigzag"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // Ichimoku tooltip
    const ichimokuIndicator = config.indicators?.find(
      (ind) => ind.key === "ichimoku",
    );
    if (ichimokuIndicator) {
      const IchimokuTooltip =
        indicatorRegistry.trend?.indicators.ichimoku?.tooltip;
      if (IchimokuTooltip) {
        tooltips.push(
          <IchimokuTooltip
            key="ichimoku"
            yAccessor={(d) => d[ichimokuIndicator.renderKey]}
            options={{
              conversionPeriod: ichimokuIndicator.config?.conversionPeriod || 9,
              basePeriod: ichimokuIndicator.config?.basePeriod || 26,
              spanBPeriod: ichimokuIndicator.config?.spanBPeriod || 52,
              displacement: ichimokuIndicator.config?.displacement || 26,
              ...ichimokuIndicator.options,
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(ichimokuIndicator)}
            data-evo-id="charttooltips-btn-ichimoku"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // Alligator tooltip
    const alligatorIndicator = config.indicators?.find(
      (ind) => ind.key === "alligator",
    );
    if (alligatorIndicator) {
      const registryEntry = findRegistryEntry(alligatorIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="alligator"
            yAccessor={(d) => d[alligatorIndicator.renderKey]}
            options={{
              jawPeriod: alligatorIndicator.config?.jawPeriod || 13,
              teethPeriod: alligatorIndicator.config?.teethPeriod || 8,
              lipsPeriod: alligatorIndicator.config?.lipsPeriod || 5,
              ...alligatorIndicator.options,
            }}
            appearance={{
              strokeJaw: alligatorIndicator.appearanceConfig?.strokeJaw || {
                color: "#0000FF",
              },
              strokeTeeth: alligatorIndicator.appearanceConfig?.strokeTeeth || {
                color: "#FF0000",
              },
              strokeLips: alligatorIndicator.appearanceConfig?.strokeLips || {
                color: "#00DD00",
              },
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(alligatorIndicator)}
            {...(registryEntry.tooltipProps || {})}
            data-evo-id="charttooltips-alligator-tooltip"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // Rainbow MA tooltip
    const rainbowMAIndicator = config.indicators?.find(
      (ind) => ind.key === "rainbowMA",
    );
    if (rainbowMAIndicator) {
      const registryEntry = findRegistryEntry(rainbowMAIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="rainbowMA"
            yAccessor={(d) => d[rainbowMAIndicator.renderKey]}
            options={{
              period: rainbowMAIndicator.config?.period || 2,
              sourcePath: rainbowMAIndicator.config?.sourcePath || "close",
              ...rainbowMAIndicator.options,
            }}
            appearance={{
              strokeSMA1: rainbowMAIndicator.appearanceConfig?.strokeSMA1 || {
                color: "#FF0000",
              },
              strokeSMA2: rainbowMAIndicator.appearanceConfig?.strokeSMA2 || {
                color: "#FF7F00",
              },
              strokeSMA3: rainbowMAIndicator.appearanceConfig?.strokeSMA3 || {
                color: "#FFFF00",
              },
              strokeSMA4: rainbowMAIndicator.appearanceConfig?.strokeSMA4 || {
                color: "#7FFF00",
              },
              strokeSMA5: rainbowMAIndicator.appearanceConfig?.strokeSMA5 || {
                color: "#00FF7F",
              },
              strokeSMA6: rainbowMAIndicator.appearanceConfig?.strokeSMA6 || {
                color: "#00FFFF",
              },
              strokeSMA7: rainbowMAIndicator.appearanceConfig?.strokeSMA7 || {
                color: "#007FFF",
              },
              strokeSMA8: rainbowMAIndicator.appearanceConfig?.strokeSMA8 || {
                color: "#0000FF",
              },
              strokeSMA9: rainbowMAIndicator.appearanceConfig?.strokeSMA9 || {
                color: "#7F00FF",
              },
              strokeSMA10: rainbowMAIndicator.appearanceConfig?.strokeSMA10 || {
                color: "#FF00FF",
              },
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(rainbowMAIndicator)}
            {...(registryEntry.tooltipProps || {})}
            data-evo-id="charttooltips-rainbowma-tooltip"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // BBI (Bullish-Bearish) tooltip — overlay de preço migrado do Silverlight
    const bbiIndicator = config.indicators?.find((ind) => ind.key === "bbi");
    if (bbiIndicator) {
      const registryEntry = findRegistryEntry(bbiIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="bbi"
            yAccessor={(d) => d[bbiIndicator.renderKey]}
            options={{
              n1: bbiIndicator.config?.n1 || 3,
              n2: bbiIndicator.config?.n2 || 6,
              n3: bbiIndicator.config?.n3 || 12,
              n4: bbiIndicator.config?.n4 || 24,
              ...bbiIndicator.options,
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            valueFill={
              bbiIndicator.appearanceConfig?.stroke?.color || "#FFFF00"
            }
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(bbiIndicator)}
            {...(registryEntry.tooltipProps || {})}
            data-evo-id="charttooltips-bbi-tooltip"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // Linear Forecast tooltip
    const linearForecastIndicator = config.indicators?.find(
      (ind) => ind.key === "linearForecast",
    );
    if (linearForecastIndicator) {
      const registryEntry = findRegistryEntry(linearForecastIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="linearForecast"
            yAccessor={(d) => d[linearForecastIndicator.renderKey]}
            options={{
              windowSize: linearForecastIndicator.config?.windowSize || 14,
              sourcePath: linearForecastIndicator.config?.sourcePath || "close",
              ...linearForecastIndicator.options,
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            valueFill={
              linearForecastIndicator.appearanceConfig?.stroke?.color ||
              "#FF6B35"
            }
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(linearForecastIndicator)}
            {...(registryEntry.tooltipProps || {})}
            data-evo-id="charttooltips-linearforecast-tooltip"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // LinRegr (Canais de Regressão Linear) tooltip — overlay, 3 linhas
    const linRegrChannelIndicator = config.indicators?.find(
      (ind) => ind.key === "linRegrChannel",
    );
    if (linRegrChannelIndicator) {
      const registryEntry = findRegistryEntry(linRegrChannelIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="linRegrChannel"
            yAccessor={(d) => d[linRegrChannelIndicator.renderKey]}
            options={{
              period: linRegrChannelIndicator.config?.period || 14,
              percent: linRegrChannelIndicator.config?.percent || 100,
              ...linRegrChannelIndicator.options,
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(linRegrChannelIndicator)}
            data-evo-id="charttooltips-linregr-tooltip"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // Fractal Chaos Bands tooltip
    const fractalChaosBandsIndicator = config.indicators?.find(
      (ind) => ind.key === "fractalChaosBands",
    );
    if (fractalChaosBandsIndicator) {
      const registryEntry = findRegistryEntry(fractalChaosBandsIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="fractalChaosBands"
            yAccessor={(d) => d[fractalChaosBandsIndicator.renderKey]}
            appearance={{
              strokeHigh: fractalChaosBandsIndicator.appearanceConfig
                ?.strokeHigh || { color: "#4CAF50" },
              strokeLow: fractalChaosBandsIndicator.appearanceConfig
                ?.strokeLow || { color: "#F44336" },
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(fractalChaosBandsIndicator)}
            data-evo-id="charttooltips-tooltip-fractal-chaos-bands"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // Linear Regression Intercept tooltip
    const linearRegressionInterceptIndicator = config.indicators?.find(
      (ind) => ind.key === "linearRegressionIntercept",
    );
    if (linearRegressionInterceptIndicator) {
      const registryEntry = findRegistryEntry(
        linearRegressionInterceptIndicator.key,
      );
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="linearRegressionIntercept"
            yAccessor={(d) => d[linearRegressionInterceptIndicator.renderKey]}
            options={{
              windowSize:
                linearRegressionInterceptIndicator.config?.windowSize || 14,
              sourcePath:
                linearRegressionInterceptIndicator.config?.sourcePath ||
                "close",
              ...linearRegressionInterceptIndicator.options,
            }}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            valueFill={
              linearRegressionInterceptIndicator.appearanceConfig?.stroke
                ?.color || "#E040FB"
            }
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(linearRegressionInterceptIndicator)}
            {...(registryEntry.tooltipProps || {})}
            data-evo-id="charttooltips-tooltip-linear-regression-intercept"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // Pivot Points tooltip
    const pivotPointsIndicator = config.indicators?.find(
      (ind) => ind.key === "pivotPoints",
    );
    if (pivotPointsIndicator) {
      const registryEntry = findRegistryEntry(pivotPointsIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="pivotPoints"
            yAccessor={(d) => d[pivotPointsIndicator.renderKey]}
            options={{
              type: pivotPointsIndicator.config?.type || "standard",
              continuous: pivotPointsIndicator.config?.continuous || false,
              ...pivotPointsIndicator.options,
            }}
            appearance={pivotPointsIndicator.appearanceConfig || {}}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(pivotPointsIndicator)}
            {...(registryEntry.tooltipProps || {})}
            data-evo-id="charttooltips-pivotpoints-tooltip"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // Valuation Lines tooltip
    const valuationLinesIndicator = config.indicators?.find(
      (ind) => ind.key === "valuationLines",
    );
    if (valuationLinesIndicator) {
      const registryEntry = findRegistryEntry(valuationLinesIndicator.key);
      const TooltipComponent = registryEntry?.tooltip;
      if (TooltipComponent) {
        tooltips.push(
          <TooltipComponent
            key="valuationLines"
            options={{
              averageType:
                valuationLinesIndicator.config?.averageType || "mean",
              sourcePath: valuationLinesIndicator.config?.sourcePath || "close",
              ...valuationLinesIndicator.options,
            }}
            appearance={valuationLinesIndicator.appearanceConfig || {}}
            origin={dynamicOrigin(currentY)}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(price) => formatNumber(price, 2)}
            onClick={createIndicatorOnClick(valuationLinesIndicator)}
            {...(registryEntry.tooltipProps || {})}
            data-evo-id="charttooltips-valuationlines-tooltip"
          />,
        );
        currentY += tooltipSpacing;
      }
    }

    // Add ChartButtonsTooltip for main chart positioned at Y=16
    // Pass config.indicators so delete button can remove all indicators
    tooltips.push(
      createChartButtonsTooltip("Main", null, config.indicators || []),
    );

    return (
      <>
        {tooltips}
        {/* Resize handle at the bottom of main chart section (not for last section) */}
        {!isLastSection && (
          <ChartResizeHandle
            chartId={chartId}
            sectionIndex={sectionIndex}
            disabled={isLastSection}
            onStartResize={onStartResize}
            onResize={onResize}
            onEndResize={onEndResize}
            onReset={onResetSize}
          />
        )}
      </>
    );
  }

  // Phase 3: Multiple indicators on same panel (config.indicators array)
  // Check this FIRST before config.indicator (which may also exist as fallback)
  if (config.indicators && config.indicators.length > 0) {
    const tooltips = [];
    const tooltipSpacing = 24; // Vertical spacing between tooltips
    let currentY = INDICATOR_TOOLTIP_ORIGIN[1];

    // Theme-aware tooltip background
    const isDarkMode = themeColors.isDark;
    // Use chart background color #1f2937 with transparency
    const tooltipBgFill = themeColors.tooltipBg;

    // Determine if we should use shared MA tooltip (only on main price chart)
    const isMainChart = config.type === "main";
    const maTypeKeys = MA_TYPES.map((ma) => ma.value);

    let i = 0;
    while (i < config.indicators.length) {
      const indicator = config.indicators[i];
      const isMA = maTypeKeys.includes(indicator.key);

      // On main chart: group consecutive MAs for shared tooltip
      // On indicator panels: render each MA individually (they likely use different sources)
      if (isMA && isMainChart) {
        // Collect all consecutive MA indicators for shared tooltip
        const maGroup = [];
        while (
          i < config.indicators.length &&
          maTypeKeys.includes(config.indicators[i].key)
        ) {
          maGroup.push(config.indicators[i]);
          i++;
        }

        // Render grouped MAs with shared MovingAverageTooltip
        const MovingAverageTooltip = indicatorRegistry.movingAverages?.tooltip;
        if (MovingAverageTooltip) {
          const tooltipOptions = maGroup.map((maIndicator) => {
            return {
              yAccessor: (d) => {
                if (!d) return undefined;
                const value = d[maIndicator.renderKey];
                if (value === undefined) {
                }
                return value;
              },
              type: getMALabel(maIndicator.key),
              stroke: maIndicator.appearanceConfig?.stroke?.color || "#2196F3",
              windowSize: maIndicator.config?.windowSize || 10,
              sourcePath: maIndicator.config?.sourcePath,
              echo: maIndicator,
            };
          });

          tooltips.push(
            <MovingAverageTooltip
              key={`ma-group-${tooltips.length}`}
              origin={[INDICATOR_TOOLTIP_ORIGIN[0], currentY]}
              className="moving-average-tooltip"
              options={tooltipOptions}
              textFill={themeColors.text}
              labelFill={themeColors.textSecondary}
              displayFormat={(value) => formatNumber(value, 2)}
            />,
          );
          currentY += tooltipSpacing;
        }
      } else {
        // Render indicator individually (including MAs on non-main charts)
        const registryEntry = findRegistryEntry(indicator.key);

        // For MA indicators on non-main charts, use SingleValueTooltip
        // For other indicators, use their registry-defined tooltip
        let TooltipComponent = registryEntry?.tooltip;
        if (!TooltipComponent && isMA) {
          TooltipComponent = SingleValueTooltip;
        }

        if (!TooltipComponent) {
          i++;
          continue;
        }
        // Use standard origin position (no extra padding needed)
        const tooltipOrigin = [INDICATOR_TOOLTIP_ORIGIN[0], currentY];

        // For MA indicators using SingleValueTooltip, prepare label
        const maLabel = isMA
          ? indicator.displayName ||
            `${getMALabel(indicator.key)}(${indicator.config?.windowSize || 10})`
          : null;

        // For RSI, pass displayName in options so RSITooltip can use it
        let tooltipOptions =
          indicator.key === "rsi"
            ? {
                ...(indicator.config || indicator.options || {}),
                windowSize: indicator.config?.windowSize || 14,
                sourcePath: indicator.config?.sourcePath || "close",
                displayName: indicator.displayName, // Pass displayName for proper label
              }
            : indicator.config || indicator.options || {};

        // For Klinger, prepare options and appearance
        let tooltipAppearance = undefined;
        if (indicator.key === "klinger") {
          const strokeKlinger = opaqueColor(
            indicator.appearanceConfig?.strokeMACD?.color || "#2196F3",
          );
          const strokeSignal = opaqueColor(
            indicator.appearanceConfig?.strokeSignal?.color || "#FF9800",
          );
          const divergenceRaw =
            indicator.appearanceConfig?.divergenceColor ||
            indicator.appearanceConfig?.fillMACD?.color ||
            indicator.appearanceConfig?.fill?.divergence ||
            strokeKlinger;
          const divergenceColor = opaqueColor(divergenceRaw);

          tooltipOptions = {
            longCycle: indicator.config?.longCycle || 55,
            shortCycle: indicator.config?.shortCycle || 34,
            signalPeriods: indicator.config?.signalPeriods || 13,
            ...indicator.options,
          };

          tooltipAppearance = {
            stroke: {
              macd: strokeKlinger,
              signal: strokeSignal,
            },
            fill: {
              divergence: divergenceColor,
            },
          };
        }

        // Push tooltip component and overlay as separate elements (no Fragment wrapper)
        tooltips.push(
          <TooltipComponent
            key={`${indicator.id || indicator.renderKey || tooltips.length}-tooltip`}
            yAccessor={(d) => {
              if (!d) return undefined;
              if (indicator.renderKey && d[indicator.renderKey] !== undefined) {
                return d[indicator.renderKey];
              }
              if (indicator.key && d[indicator.key] !== undefined) {
                return d[indicator.key];
              }
              return undefined;
            }}
            yLabel={maLabel || registryEntry?.displayName || indicator.key}
            yDisplayFormat={(value) => formatNumber(value, 2)}
            options={tooltipOptions}
            appearance={tooltipAppearance}
            origin={tooltipOrigin}
            className="indicator-tooltip clickable-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            bgFill={tooltipBgFill}
            valueFill={getValueFillColor(indicator.appearanceConfig)}
            // Pass volume colors for VolumeTooltip
            upColor={indicator.appearanceConfig?.upColor?.color}
            downColor={indicator.appearanceConfig?.downColor?.color}
            // Pass volume indicator config for dynamic label
            volumePathConfig={indicator.config}
            displayFormat={(value) => formatNumber(value, 2)}
            // Make tooltip clickable to open edit modal
            onClick={(e) => {
              if (indicatorMenuRef?.current?.edit) {
                indicatorMenuRef.current.edit(indicator);
              } else {
                console.error(
                  "[ChartTooltips] indicatorMenuRef.current.edit not available",
                );
              }
            }}
            // Pass tooltip props from registry, but resolve yLabel function if present
            {...(() => {
              const props = registryEntry?.tooltipProps || {};
              // If yLabel is a function, call it with the indicator config
              if (props.yLabel && typeof props.yLabel === "function") {
                return {
                  ...props,
                  yLabel: props.yLabel(indicator.config),
                };
              }
              return props;
            })()}
            data-evo-id={`charttooltips-overlay-${indicator.key}`}
          />,
        );
        // Add invisible clickable overlay for better click detection
        tooltips.push(
          <GenericChartComponent
            key={`${indicator.id || indicator.renderKey || tooltips.length}-overlay`}
            svgDraw={() => (
              <rect
                x={tooltipOrigin[0] - 2}
                y={tooltipOrigin[1] - 2}
                width={200}
                height={20}
                fill="rgba(100, 150, 255, 0)"
                stroke="none"
                style={{ cursor: "pointer" }}
                className="tooltip-hover-bg"
                onMouseEnter={(e) => {
                  e.currentTarget.setAttribute(
                    "fill",
                    "rgba(100, 150, 255, 0.1)",
                  );
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.setAttribute(
                    "fill",
                    "rgba(100, 150, 255, 0)",
                  );
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (indicatorMenuRef?.current?.edit) {
                    indicatorMenuRef.current.edit(indicator);
                  }
                }}
                data-evo-id="generic-chartcomponent-tooltip-overlay"
              />
            )}
            drawOn={["pan"]}
          />,
        );
        currentY += tooltipSpacing;
        i++;
      }
    }

    return wrapWithButtons(<>{tooltips}</>);
  }

  // Indicator chart tooltip (single indicator per chart - legacy path)
  // config.indicator is expected for non-main charts with single indicator
  if (config.indicator) {
    const registryEntry = findRegistryEntry(config.indicator.key);

    let tooltipElement = null;

    if (registryEntry?.tooltip) {
      const TooltipComponent = registryEntry.tooltip;

      // Special handling for RSI indicator to pass correct options
      if (config.indicator.key === "rsi") {
        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => d[config.indicator.renderKey]}
            options={{
              windowSize: config.indicator.config?.windowSize || 14,
              sourcePath: config.indicator.config?.sourcePath || "close",
              // Pass displayName for proper label (e.g., "RSI(14,EMA(10))")
              displayName: config.indicator.displayName,
              ...config.indicator.options,
            }}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            valueFill={getValueFillColor(config.indicator.appearanceConfig)}
            displayFormat={(value) => formatNumber(value, 2)}
            // Pass tooltip props from registry
            {...(registryEntry.tooltipProps || {})}
          />
        );
      }

      // Special handling for MFI indicator to pass correct options
      else if (config.indicator.key === "mfi") {
        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => d[config.indicator.renderKey]}
            options={{
              windowSize: config.indicator.config?.windowSize || 14,
              ...config.indicator.options,
            }}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            valueFill={getValueFillColor(config.indicator.appearanceConfig)}
            displayFormat={(value) => formatNumber(value, 2)}
            // Pass tooltip props from registry
            {...(registryEntry.tooltipProps || {})}
          />
        );
      }

      // Special handling for MACD indicator to pass correct options and appearance
      else if (config.indicator.key === "macd") {
        const strokeMacd = opaqueColor(
          config.indicator.appearanceConfig?.strokeMACD?.color || "#FF0000",
        );
        const strokeSignal = opaqueColor(
          config.indicator.appearanceConfig?.strokeSignal?.color || "#00F300",
        );
        const divergenceRaw =
          config.indicator.appearanceConfig?.divergenceColor ||
          config.indicator.appearanceConfig?.fillMACD?.color ||
          config.indicator.appearanceConfig?.fill?.divergence ||
          strokeMacd;
        const divergenceColor = opaqueColor(divergenceRaw);

        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => d[config.indicator.renderKey]}
            options={{
              slow: config.indicator.config?.slow || 26,
              fast: config.indicator.config?.fast || 12,
              signal: config.indicator.config?.signal || 9,
              ...config.indicator.options,
            }}
            appearance={{
              stroke: {
                macd: strokeMacd,
                signal: strokeSignal,
              },
              fill: {
                divergence: divergenceColor,
              },
            }}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            displayFormat={(value) => formatNumber(value, 2)}
          />
        );
      }

      // Special handling for Klinger indicator to pass correct options and appearance
      else if (config.indicator.key === "klinger") {
        const strokeKlinger = opaqueColor(
          config.indicator.appearanceConfig?.strokeMACD?.color || "#2196F3",
        );
        const strokeSignal = opaqueColor(
          config.indicator.appearanceConfig?.strokeSignal?.color || "#FF9800",
        );
        const divergenceRaw =
          config.indicator.appearanceConfig?.divergenceColor ||
          config.indicator.appearanceConfig?.fillMACD?.color ||
          config.indicator.appearanceConfig?.fill?.divergence ||
          strokeKlinger;
        const divergenceColor = opaqueColor(divergenceRaw);

        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => d[config.indicator.renderKey]}
            options={{
              longCycle: config.indicator.config?.longCycle || 55,
              shortCycle: config.indicator.config?.shortCycle || 34,
              signalPeriods: config.indicator.config?.signalPeriods || 13,
              ...config.indicator.options,
            }}
            appearance={{
              stroke: {
                macd: strokeKlinger,
                signal: strokeSignal,
              },
              fill: {
                divergence: divergenceColor,
              },
            }}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            displayFormat={(value) => formatNumber(value, 2)}
          />
        );
      }

      // Special handling for Volume Oscillator indicator to pass correct options and appearance
      else if (config.indicator.key === "volumeOscillator") {
        const strokeColor = opaqueColor(
          config.indicator.appearanceConfig?.stroke?.color || "#2196F3",
        );

        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => d[config.indicator.renderKey]}
            options={{
              shortCycle: config.indicator.config?.shortCycle || 12,
              longCycle: config.indicator.config?.longCycle || 26,
              pointsOrPercent:
                config.indicator.config?.pointsOrPercent || "Points",
              ...config.indicator.options,
            }}
            appearance={{
              stroke: {
                color: strokeColor,
              },
            }}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(value) => formatNumber(value, 2)}
          />
        );
      }

      // Special handling for RVI indicator to pass correct options and appearance
      else if (config.indicator.key === "rvi") {
        const strokeRvi = opaqueColor(
          config.indicator.appearanceConfig?.strokeMACD?.color || "#AAAAAA",
        );
        const strokeSignal = opaqueColor(
          config.indicator.appearanceConfig?.strokeSignal?.color || "#FF0000",
        );
        const divergenceColor = opaqueColor(
          config.indicator.appearanceConfig?.barHistogram?.increasingColor ||
            "#8cc176",
        );

        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => d[config.indicator.renderKey]}
            options={{
              period: config.indicator.config?.period || 10,
              ...config.indicator.options,
            }}
            appearance={{
              strokeMACD: { color: strokeRvi },
              strokeSignal: { color: strokeSignal },
              barDivergence: { color: divergenceColor },
            }}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            displayFormat={(value) => formatNumber(value, 2)}
          />
        );
      } else if (config.indicator.key === "adx") {
        const appearanceConfig = config.indicator.appearanceConfig || {};
        const plusColor = appearanceConfig.positiveDIStroke?.color || "#00FF00";
        const minusColor =
          appearanceConfig.negativeDIStroke?.color || "#FF0000";
        const adxColor = appearanceConfig.adxStroke?.color || "#FFD700";
        const histogramPositive =
          appearanceConfig.positiveHistogramColor?.color || "#00DD00";
        const histogramNegative =
          appearanceConfig.negativeHistogramColor?.color || "#FF0000";

        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => d[config.indicator.renderKey]}
            options={{
              windowSize: config.indicator.config?.windowSize || 14,
              smoothingPeriod: config.indicator.config?.smoothingPeriod || 14,
              ...config.indicator.options,
            }}
            appearance={{
              plusDI: plusColor,
              minusDI: minusColor,
              adx: adxColor,
              histogramPositive,
              histogramNegative,
            }}
            showSeries={
              appearanceConfig.showSeries !== undefined
                ? appearanceConfig.showSeries
                : true
            }
            showHistogram={!!appearanceConfig.showHistogram}
            showShading={!!appearanceConfig.showShading}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(value) => formatNumber(value, 2)}
          />
        );
      } else if (config.indicator.key === "hiloActivator") {
        const appearanceConfig = config.indicator.appearanceConfig || {};
        const longColor = appearanceConfig.longStroke?.color || "#2ecc71";
        const shortColor = appearanceConfig.shortStroke?.color || "#ff4d4d";

        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => d[config.indicator.renderKey]}
            options={{
              highPeriod: config.indicator.config?.highPeriod || 3,
              lowPeriod: config.indicator.config?.lowPeriod || 3,
            }}
            longFill={longColor}
            shortFill={shortColor}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(value) => formatNumber(value, 2)}
          />
        );
      } else if (config.indicator.key === "didi") {
        const appearanceConfig = config.indicator.appearanceConfig || {};
        const longColor = appearanceConfig.longStroke?.color || "#ff0000";
        const shortColor = appearanceConfig.shortStroke?.color || "#00ff00";

        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => d[config.indicator.renderKey]}
            options={{
              highPeriod: config.indicator.config?.highPeriod || 3,
              midPeriod: config.indicator.config?.midPeriod || 8,
              lowPeriod: config.indicator.config?.lowPeriod || 20,
              ...config.indicator.options,
            }}
            longFill={longColor}
            shortFill={shortColor}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(value) => formatNumber(value, 2)}
          />
        );
      } else if (config.indicator.key === "supertrend") {
        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => {
              const node = d?.[config.indicator.renderKey];
              if (!node || typeof node !== "object") return undefined;
              return Number.isFinite(node.value) ? node.value : undefined;
            }}
            options={{
              period: config.indicator.config?.period ?? 7,
              multiplier: config.indicator.config?.multiplier ?? 3,
              ...config.indicator.options,
            }}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            valueFill={getValueFillColor(config.indicator.appearanceConfig)}
            displayFormat={(value) => formatNumber(value, 2)}
            {...(registryEntry.tooltipProps || {})}
          />
        );
      } else if (config.indicator.key === "trix") {
        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => d[config.indicator.renderKey]}
            options={{
              period: config.indicator.config?.period || 14,
              ...config.indicator.options,
            }}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            valueFill={getValueFillColor(config.indicator.appearanceConfig)}
            displayFormat={(value) => formatNumber(value, 2)}
          />
        );
      }

      // Special handling for stochastic indicator to support appearance prop
      else if (config.indicator.key === "stochastic") {
        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => d[config.indicator.renderKey]}
            options={{
              windowSize: config.indicator.config?.windowSize || 12,
              kWindowSize: config.indicator.config?.kWindowSize || 3,
              dWindowSize: config.indicator.config?.dWindowSize || 3,
              ...config.indicator.options,
            }}
            appearance={
              config.indicator.appearanceConfig?.appearance || {
                stroke: {
                  kLine:
                    config.indicator.appearanceConfig?.stroke?.kLine ||
                    "#ff7f0e",
                  dLine:
                    config.indicator.appearanceConfig?.stroke?.dLine ||
                    "#17becf",
                },
              }
            }
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            label="Stochastic"
            displayFormat={(d) => formatNumber(d, 2)}
            // Pass tooltip props from registry
            {...(registryEntry.tooltipProps || {})}
          />
        );
      }

      // Special handling for Aroon indicator
      else if (config.indicator.key === "aroon") {
        const appearanceConfig = config.indicator.appearanceConfig || {};

        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => d[config.indicator.renderKey]}
            options={{
              period: config.indicator.config?.period || 14,
              ...config.indicator.options,
            }}
            appearance={{
              strokeAroonUp: appearanceConfig.strokeAroonUp || {
                color: "#00DD00",
              },
              strokeAroonDown: appearanceConfig.strokeAroonDown || {
                color: "#FF0000",
              },
            }}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(value) => formatNumber(value, 2)}
          />
        );
      }

      // Special handling for MA Deviation indicator to pass correct options
      else if (config.indicator.key === "maDeviation") {
        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => d[config.indicator.renderKey]}
            options={{
              windowSize: config.indicator.config?.windowSize || 12,
              sourcePath: config.indicator.config?.sourcePath || "close",
              maType: config.indicator.config?.maType || "sma",
              mode: config.indicator.config?.mode || "Points",
              ...config.indicator.options,
            }}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(value) => formatNumber(value, 2)}
          />
        );
      }

      // Special handling for Bollinger Bandwidth indicator to pass correct options
      else if (config.indicator.key === "bollingerBandwidth") {
        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => d[config.indicator.renderKey]}
            options={{
              windowSize: config.indicator.config?.windowSize || 20,
              multiplier: config.indicator.config?.multiplier || 2,
              sourcePath: config.indicator.config?.sourcePath || "close",
              movingAverageType:
                config.indicator.config?.movingAverageType || "sma",
              ...config.indicator.options,
            }}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            displayFormat={(value) => formatNumber(value, 2)}
          />
        );
      }

      // Default case for other indicators
      else {
        tooltipElement = (
          <TooltipComponent
            yAccessor={(d) => {
              if (!d) return undefined;
              if (
                config.indicator.renderKey &&
                d[config.indicator.renderKey] !== undefined
              ) {
                return d[config.indicator.renderKey];
              }
              if (
                config.indicator.key &&
                d[config.indicator.key] !== undefined
              ) {
                return d[config.indicator.key];
              }
              return undefined;
            }}
            options={config.indicator.options || {}}
            origin={INDICATOR_TOOLTIP_ORIGIN}
            className="indicator-tooltip"
            labelFill={themeColors.textSecondary}
            textFill={themeColors.text}
            // Extract color from first appearance config item for SingleValueTooltip
            valueFill={getValueFillColor(config.indicator.appearanceConfig)}
            // Pass volume colors for VolumeTooltip
            upColor={config.indicator.appearanceConfig?.upColor?.color}
            downColor={config.indicator.appearanceConfig?.downColor?.color}
            // Pass volume indicator config for dynamic label (NEW)
            volumePathConfig={config.indicator.config}
            displayFormat={(value) => formatNumber(value, 2)}
            // Pass tooltip props from registry, but resolve yLabel function if present
            {...(() => {
              const props = registryEntry.tooltipProps || {};
              // If yLabel is a function, call it with the indicator config
              if (props.yLabel && typeof props.yLabel === "function") {
                return {
                  ...props,
                  yLabel: props.yLabel(config.indicator.config),
                };
              }
              return props;
            })()}
          />
        );
      }
    }

    // Always wrap with buttons and resize handle, even if there's no tooltip
    return wrapWithButtons(tooltipElement);
  }

  return null;
};

ChartTooltips.propTypes = {
  config: PropTypes.object.isRequired,
  indicatorRegistry: PropTypes.object.isRequired,
  themeColors: PropTypes.object.isRequired,
  xFormat: PropTypes.func,
  periodicity: PropTypes.string,
  chartId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  chartSymbol: PropTypes.string,
  sectionIndex: PropTypes.number.isRequired,
  canMoveUp: PropTypes.bool.isRequired,
  canMoveDown: PropTypes.bool.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  indicatorMenuRef: PropTypes.shape({
    current: PropTypes.shape({ edit: PropTypes.func, remove: PropTypes.func }),
  }),
  // Resize handle props
  isLastSection: PropTypes.bool,
  onStartResize: PropTypes.func,
  onResize: PropTypes.func,
  onEndResize: PropTypes.func,
  onResetSize: PropTypes.func,
};

export default ChartTooltips;
